import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import * as yup from "yup";
import { getCustomerList, getActiveSurveyTypesByCustomer } from "requests/customer";
import { MSpinner } from "components/Shared";
import MCard from "components/Shared/MCard.tsx";
import { DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import OrderStepButtons from "components/WebOrderV2/OrderStepButtons";


const Product = props => {
  const [accounts, setAccounts] = useState([]);
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const mounted = useRef(true);
  const mCardControlMethodsProduct = useRef({});

  useEffect(() => {
    getCustomerList(payload => handleAccounts(payload), mounted);
    if (props.data.customer) {
      getActiveSurveyTypesByCustomer(props.data.customer.value, payload => setSurveyTypes(payload), mounted);
    }

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isLoading && props.handlingFeatures) {
      setIsLoading(false);
    }
  }, [props.handlingFeatures]);

  const setControlMethodsProduct = () => {
    return cm => (mCardControlMethodsProduct.current = cm);
  };

  const handleAccounts = accountList => {
    setAccounts(accountList);
    if (accountList.length === 1 && !props.data.customer) {
      mCardControlMethodsProduct.current.setEditData("customer", { label: accountList[0].displayLabel, value: accountList[0].number });
    }
  };
  
  const handleAccountChange = value => {
    // If you delete all the characters in the drop down while searching, then hit backspace again, we get an empty array as the value
    if (Array.isArray(value) && value.length === 0) return;
    let editData = mCardControlMethodsProduct.current.getEditedDataObject();
    if (editData.surveyType) {
      mCardControlMethodsProduct.current.setEditData("surveyType", null);
    }
    if (value) {
      props.onAccountChange(value);
      setIsLoading(true);
      getActiveSurveyTypesByCustomer(value.value, handleSurveyTypes, mounted);
    }
  };

  const handleSurveyTypes = response => {
    setSurveyTypes(response);
    setIsLoading(false);
  };

  const handleSurveyTypeChange = value => {
    if (Array.isArray(value) && value.length === 0) return;
    if (value) {
      props.onSurveyTypeChange(value);
    } 
  };

  const getAccountSelection = () => {
    if (props.data.customer) return props.data.customer;
    // If user only has access to 1 account, make it selected by default
    if (accounts.length === 1) return { label: accounts[0].displayLabel, value: accounts[0].number };
    return null;
  };

  const getAccountSurveyTypeFields = () => {
    return [
      {
        key: "customer",
        dataType: DATA_TYPE_DROPDOWN2,
        label: "Account",
        value: getAccountSelection(),
        dataTypeOptions: { options: accounts.map(x => ({ label: x.displayLabel, value: x.number })) },
        validation: yup.string().nullable().required("Account is required"),
        placeholder: "Select... (required)",
        onChange: handleAccountChange
      },
      {
        key: "surveyType",
        dataType: DATA_TYPE_DROPDOWN2,
        label: "Report Type",
        value: props.data.surveyType,
        dataTypeOptions: { options: surveyTypes.map(x => ({ label: x.name, value: x.code, id: x.id })), },
        validation: yup.string().nullable().required("Report Type is required"),
        placeholder: "Select... (required)",
        onChange: handleSurveyTypeChange,
      },
    ];
  };

  // Only onNext is implemented here as this is intended to be the first step
  const onNext = () => {
    let isProductValid = mCardControlMethodsProduct.current.runValidation(true);
    
    if (isProductValid) {
      let productData = mCardControlMethodsProduct.current.getEditedDataObject();
      let editedData = { ...productData };
      let accountItem = accounts.filter(a => a.number === editedData.customer);
      // Need to get the whole object for these dropdowns or MSelect won't properly set the existing value...
      if (accountItem.length) {
        editedData.customer = accountItem.map(x => ({ label: x.displayLabel, value: x.number }))[0];
      }
      let surveyType = surveyTypes.filter(s => s.code === editedData.surveyType);
      if (surveyType.length) {
        editedData.surveyType = surveyType.map(x => ({ label: x.name, value: x.code, id: x.id }))[0];
      }
      props.onNext(editedData);
    }
  };  

  return (
    <>
      <MCard
        title="Account and Report Type"
        getControlMethods={setControlMethodsProduct("product")}
        fields={isLoading ? [] : getAccountSurveyTypeFields()}
        onlyEditable
      >
        {isLoading ? <MSpinner/> : null}
      </MCard>
      <OrderStepButtons
        currentStep={props.currentStep}
        onPrevious={() => {}}
        onNext={onNext}
      />
    </>
  );
};

export default Product;

Product.propTypes = {
  currentStep: PropTypes.number,  // Current step in order process, needed for OrderStepButtons
  onAccountChange: PropTypes.func,
  onSurveyTypeChange: PropTypes.func,
  data: PropTypes.object, // Data for MCard fields
  onNext: PropTypes.func, // Func to call when fields are valid to move to the next step
  terminology: PropTypes.string, // Insurance or Lender, see typeConstants/webOrder.js
  handlingFeatures: PropTypes.object,  // caseHandlingFeatures from settings
  stateOptions: PropTypes.array,  // State abbrevation options for address field
};